import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { ProgressSyncInput, useWatchSync } from '../hooks/useWatchSync';
import { loadProfileStorage, migrateAllProfileRecords, profileStorageKey } from '../utils/profileStorage';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProgressEntry {
  /** Current playback position in seconds */
  position: number;
  /** Total duration in seconds */
  duration: number;
  /** ms timestamp of last save */
  updatedAt: number;
  /** Content type — used for backend sync */
  streamType?: 'movie' | 'series_episode';
}

interface WatchProgressContextType {
  getProgress: (id: string, streamType: ProgressSyncInput['streamType']) => ProgressEntry | null;
  setProgress: (id: string, position: number, duration: number, streamType: ProgressSyncInput['streamType']) => void;
  clearProgress: (id: string, streamType: ProgressSyncInput['streamType']) => void;
  clearAllLocal: () => void;
  /** Flush the durable V2 queue immediately. */
  flushProgress: () => void;
  /** Re-fetch progress from the authoritative V2 snapshot. */
  refreshFromServer: () => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const WatchProgressContext = createContext<WatchProgressContextType>({
  getProgress: () => null,
  setProgress: () => {},
  clearProgress: () => {},
  clearAllLocal: () => {},
  flushProgress: () => {},
  refreshFromServer: () => {},
});

const STORAGE_KEY_PREFIX = 'ott_player_watch_progress:';

function progressRecordKey(id: string, item: ProgressEntry): string {
  if (id.startsWith('movie:') || id.startsWith('series_episode:')) return id;
  // Untyped legacy entries were saved as movies; guessing an episode would copy the wrong resume point.
  return `${item?.streamType ?? 'movie'}:${id}`;
}

function loadProgress(storageKey: string, legacyKey: string | null, hasProfile: boolean): Record<string, ProgressEntry> {
  try {
    const raw = hasProfile
      ? migrateAllProfileRecords<ProgressEntry>(storageKey, STORAGE_KEY_PREFIX, progressRecordKey)
      : loadProfileStorage(storageKey, legacyKey);
    const parsed = JSON.parse(raw || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const next: Record<string, ProgressEntry> = {};
    for (const [id, entry] of Object.entries(parsed) as [string, ProgressEntry][]) {
      if (!entry || typeof entry !== 'object') continue;
      const key = progressRecordKey(id, entry);
      if (!next[key] || Number(entry.updatedAt || 0) >= Number(next[key].updatedAt || 0)) next[key] = entry;
    }
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* keep usable data in memory */ }
    return next;
  } catch {
    return {};
  }
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export const WatchProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authData } = useAuth();
  const { canSync, fetchRemote, pushProgress, removeProgress } = useWatchSync();
  const username = authData?.user?.username;
  const storageKey = profileStorageKey(STORAGE_KEY_PREFIX, authData?.profileId, username);
  const legacyStorageKey = username ? `${STORAGE_KEY_PREFIX}${username}` : null;

  const [data, setData] = useState<Record<string, ProgressEntry>>(() => {
    if (!storageKey) return {};
    return loadProgress(storageKey, legacyStorageKey, !!authData?.profileId);
  });

  // Reload when user changes (login / logout)
  useEffect(() => {
    if (!storageKey) { setData({}); return; }
    setData(loadProgress(storageKey, legacyStorageKey, !!authData?.profileId));
  }, [authData?.profileId, legacyStorageKey, storageKey]);

  // On mount / account change: fetch remote progress and REPLACE local (backend is source of truth)
  const syncedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = storageKey;
    if (!canSync || !key || syncedRef.current === key) return;
    syncedRef.current = key;
    fetchRemote().then((remote) => {
      if (!remote) return; // network failure — keep local
      const next: Record<string, ProgressEntry> = {};
      for (const item of remote.progress) {
        next[`${item.streamType}:${item.streamId}`] = {
          position:   item.position,
          duration:   item.duration,
          updatedAt:  item.updatedAt,
          streamType: item.streamType,
        };
      }
      setData(next);
      try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* ignore */ }
    }).catch(() => { /* network error — local data stays */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSync, storageKey]);

  const persist = useCallback(
    (next: Record<string, ProgressEntry>) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // localStorage full or unavailable — fail silently
      }
    },
    [storageKey],
  );

  const getProgress = useCallback(
    (id: string, streamType: ProgressSyncInput['streamType']): ProgressEntry | null => data[`${streamType}:${id}`] ?? null,
    [data],
  );

  const setProgress = useCallback(
    (id: string, position: number, duration: number, streamType: ProgressSyncInput['streamType']) => {
      if (!duration || duration < 10) return; // ignore very short / unloaded content
      setData((prev) => {
        const next = { ...prev };
        const updatedAt = Date.now();
        next[`${streamType}:${id}`] = { position, duration, updatedAt, streamType };
        // Push updated progress to backend (debounced 30s)
        const item: ProgressSyncInput = {
          streamId: id,
          streamType,
          position,
          duration,
        };
        pushProgress([item]);
        persist(next);
        return next;
      });
    },
    [persist, pushProgress],
  );

  const clearProgress = useCallback(
    (id: string, streamType: ProgressSyncInput['streamType']) => {
      const key = `${streamType}:${id}`;
      setData((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        persist(next);
        return next;
      });
      removeProgress(id, streamType);
    },
    [persist, removeProgress],
  );

  const clearAllLocal = useCallback(() => {
    setData({});
    if (storageKey) {
      try { localStorage.setItem(storageKey, JSON.stringify({})); } catch { /* ignore */ }
    }
  }, [storageKey]);

  const flushProgress = useCallback(() => {
    if (!canSync) return;
    void fetchRemote();
  }, [canSync, fetchRemote]);

  const refreshFromServer = useCallback(() => {
    if (!canSync || !storageKey) return;
    fetchRemote().then((remote) => {
      if (!remote) return;
      const next: Record<string, ProgressEntry> = {};
      for (const item of remote.progress) {
        next[`${item.streamType}:${item.streamId}`] = {
          position: item.position,
          duration: item.duration,
          updatedAt: item.updatedAt,
          streamType: item.streamType,
        };
      }
      setData(next);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
    }).catch(() => { /* network error — keep local */ });
  }, [canSync, fetchRemote, storageKey]);

  return (
    <WatchProgressContext.Provider value={{ getProgress, setProgress, clearProgress, clearAllLocal, flushProgress, refreshFromServer }}>
      {children}
    </WatchProgressContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useWatchProgress = () => useContext(WatchProgressContext);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formats a position in seconds to a human-readable string.
 * - If >= 1 hour: "HH:MM"
 * - Otherwise:    "MM:SS"
 */
export function formatProgressTime(seconds: number): string {
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}
